import { Button, Input, InputNumber, Modal, Select, Table, Tooltip } from 'antd'
import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import { TriState } from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import {
  DamageMechanism,
  ZoneTabPrefillMeta,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { isMissingPrefillValue } from 'features/libra/stepComponents/zoneAnalysis/zoneTabAutofill'
import {
  rateUnitCode,
  storedRateUnit,
  toDisplayValue,
  toStoredValue,
} from 'features/libra/stepComponents/zoneAnalysis/zoneUnits'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import { useGeckoLocale } from 'features/locale'
import { Expand, Plus, Trash2 } from 'lucide-react'
import { FC, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoMeasurementUnit } from 'types'
import { v4 } from 'uuid'

import { AiPrefillFieldState, FieldStateTag } from './AiPrefillFieldState'
import { useCursorRestore } from './useCursorRestore'

const { TextArea } = Input

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HelpText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  margin: 0 0 12px 0;
`

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
`

const AiFieldArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const COMMON_DAMAGE_MECHANISMS = [
  'Sulfidation',
  'Wet HS Damage (Blistering/HIC/SOHIC/SSC)',
  'Creep and Stress Rupture',
  'High-temperature H2/H2S Corrosion',
  'Polythionic Acid Stress Corrosion Cracking',
  'Naphthenic Acid Corrosion',
  'Ammonium Bisulfide Corrosion (Alkaline Sour Water)',
  'Ammonium Chloride Corrosion',
  'Hydrochloric Acid Corrosion',
  'High-temperature Hydrogen Attack',
  'Oxidation',
  'Thermal Fatigue',
  'Sour Water Corrosion (Acidic)',
  'Refractory Degradation',
  'Graphitization',
  'Temper Embrittlement',
  'Decarburization',
  'Caustic Stress Corrosion Cracking',
  'Caustic Corrosion',
  'Erosion/Erosion-Corrosion',
  'Carbonate Stress Corrosion Cracking (ACSCC)',
  'Amine Stress Corrosion Cracking',
  'Chloride Stress Corrosion Cracking',
  'Carburization',
  'Hydrogen Embrittlement',
  'Thermal Shock',
  'Cavitation',
  'Graphitic Corrosion of Cast Irons',
  'Short-term Overheating—Stress Rupture (Including Steam Blanketing)',
  'Brittle Fracture',
  'Sigma Phase Embrittlement',
  '885°F ( 475°C) Embrittlement',
  'Spheroidization (Softening)',
  'Stress Relaxation Cracking (Reheat',
  'Sulfuric Acid Corrosion',
  'Hydrofluoric Acid Corrosion',
  'Flue Gas Dew Point Corrosion',
  'Dissimilar Metal Weld Cracking',
  'Hydrogen Stress Cracking in Hydrofluoric Acid',
  'Dealloying (Dezincification; Denickelification)',
  'CO2 Corrosion',
  'Corrosion Fatigue',
  'Fuel Ash Corrosion',
  'Amine Corrosion',
  'CUI - Corrosion Under Insulation',
  'Atmospheric Corrosion',
  'Ammonia Stress Corrosion Cracking',
  'Cooling Water Corrosion',
  'Boiler Water and Steam Condensate Corrosion',
  'Microbiologically Influenced Corrosion',
  'Liquid Metal Embrittlement',
  'Galvanic Corrosion',
  'Mechanical Fatigue (Includes Vibration Fatigue)',
  'Nitriding',
  'Titanium Hydriding',
  'Soil Corrosion',
  'Metal Dusting',
  'Strain Aging',
  'Phosphoric Acid Corrosion',
  'Phenol (Carbolic Acid) Corrosion',
  'Ethanol Stress Corrosion Cracking',
  'Gaseous Oxygen-enhanced Ignition and Combustion',
  'Aqueous Organic Acid Corrosion',
  'Brine Corrosion',
  'Concentration Cell Corrosion (Under-deposit)',
  'Hydrofluoric Acid Stress Corrosion Cracking of Nickel Alloys',
  'Oxygenated Water Corrosion (Non-boiler)',
  'Unspecified Internal Corrosion',
  'Methanol Stress Corrosion Cracking',
  'Chloride Stress Corrosion Cracking (Insulated)',
  'Chloride Stress Corrosion Cracking (Uninsulated)',
  'Non-Boiler Oxygen Pitting',
  'Under Deposit Corrosion',
  'General Corrosion',
  'Pitting Corrosion',
  'Flow Accelerated Corrosion (FAC)',
  'Crevice Corrosion',
  'Other',
]

export interface DamageMechanismsPanelProps {
  mechanisms: DamageMechanism[]
  onChange: (mechanisms: DamageMechanism[]) => void
  readonly?: boolean
  zoneId: string
  aiPrefill?: ZoneTabPrefillMeta
}

export const DamageMechanismsPanel: FC<DamageMechanismsPanelProps> = ({
  mechanisms,
  onChange,
  readonly,
  zoneId,
  aiPrefill,
}) => {
  const stepId = useActiveStepId()
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [expandedJustification, setExpandedJustification] = useState<{
    id: string
    name: string
  } | null>(null)
  const { saveCursor, createRestoreRef, hasRestoredRef } = useCursorRestore()
  // Imperial corrosion rates are read in mils per year, not in/yr
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const rateUnit = getPreferredMeasurementUnit(GeckoMeasurementUnit.MilsPerYear)

  const handleAdd = useCallback(() => {
    const newMechanism: DamageMechanism = {
      id: v4().substring(0, 6),
      name: '',
      rate_value: undefined,
      rate_unit: 'mm/y',
      susceptibility: undefined,
      justification: '',
    }
    onChange([...mechanisms, newMechanism])
    setEditingKey(newMechanism.id)
  }, [mechanisms, onChange])

  const handleDelete = useCallback(
    (id: string) => {
      onChange(mechanisms.filter((m) => m.id !== id))
    },
    [mechanisms, onChange]
  )

  const handleUpdate = useCallback(
    (id: string, updates: Partial<DamageMechanism>) => {
      onChange(mechanisms.map((m) => (m.id === id ? { ...m, ...updates } : m)))
    },
    [mechanisms, onChange]
  )

  const columns = useMemo(
    () => [
      {
        title: 'Damage Mechanism',
        dataIndex: 'name',
        key: 'name',
        width: '25%',
        render: (text: string, record: DamageMechanism) => {
          const dmAnchor = {
            type: 'damage-mechanism' as const,
            stepId,
            zoneId,
            damageMechanismId: record.id,
          }

          if (editingKey === record.id) {
            return (
              <CommentIndicator anchor={dmAnchor}>
                <Select
                  value={text || undefined}
                  onChange={(value) =>
                    handleUpdate(record.id, { name: value ?? '' })
                  }
                  placeholder='Select mechanism'
                  style={{ width: '100%' }}
                  showSearch
                  allowClear>
                  {COMMON_DAMAGE_MECHANISMS.map((dm) => (
                    <Select.Option key={dm} value={dm}>
                      {dm}
                    </Select.Option>
                  ))}
                </Select>
              </CommentIndicator>
            )
          }
          return (
            <CommentIndicator anchor={dmAnchor}>
              {text ? (
                <Tooltip title={text} mouseEnterDelay={0.1}>
                  <span>{text}</span>
                </Tooltip>
              ) : (
                <span style={{ color: '#999' }}>Not set</span>
              )}
            </CommentIndicator>
          )
        },
      },
      {
        title: `Rate (${rateUnit.name})`,
        key: 'rate',
        width: '22%',
        render: (_: unknown, record: DamageMechanism) => {
          // Edits save in the row's own unit, so the toggle never rewrites
          // rate_unit. A rate in an unrecognized unit can't be converted, so
          // it is flagged until the SME re-enters it.
          const storedUnit = storedRateUnit(record.rate_unit)
          const rawRate = `${record.rate_value ?? ''} ${record.rate_unit}`
          const displayRate = storedUnit
            ? toDisplayValue(record.rate_value, storedUnit, rateUnit)
            : undefined
          if (editingKey === record.id) {
            return (
              <InputNumber
                value={displayRate}
                onChange={(value) =>
                  handleUpdate(
                    record.id,
                    storedUnit
                      ? {
                          rate_value: toStoredValue(
                            value,
                            storedUnit,
                            rateUnit
                          ),
                        }
                      : {
                          rate_value: value ?? undefined,
                          rate_unit: rateUnitCode(rateUnit),
                        }
                  )
                }
                placeholder={storedUnit ? '0.05' : rawRate}
                style={{ width: '100%' }}
                controls={false}
              />
            )
          }
          if (!storedUnit)
            return (
              <>
                {rawRate}
                <Tooltip title='Unrecognized unit, re-enter the rate'>
                  <FieldStateTag color='gold'>Needs input</FieldStateTag>
                </Tooltip>
              </>
            )
          return displayRate ? (
            `${displayRate} ${rateUnit.name}`
          ) : (
            <span style={{ color: '#999' }}>—</span>
          )
        },
      },
      {
        title: 'Susceptibility',
        dataIndex: 'susceptibility',
        key: 'susceptibility',
        width: '13%',
        render: (_text: string | undefined, record: DamageMechanism) => {
          if (editingKey === record.id) {
            return (
              <Select<TriState>
                value={record.susceptibility}
                onChange={(value) =>
                  handleUpdate(record.id, {
                    susceptibility: value ?? undefined,
                  })
                }
                placeholder='Select'
                style={{ width: '100%' }}
                allowClear>
                <Select.Option value='low'>Low</Select.Option>
                <Select.Option value='medium'>Medium</Select.Option>
                <Select.Option value='high'>High</Select.Option>
              </Select>
            )
          }
          const text = record.susceptibility
          if (!text) return <span style={{ color: '#999' }}>—</span>

          const color =
            text === 'high' ? 'red' : text === 'medium' ? 'orange' : 'green'
          return (
            <span
              style={{ color, textTransform: 'capitalize', fontWeight: 500 }}>
              {text}
            </span>
          )
        },
      },
      {
        title: 'Justification',
        dataIndex: 'justification',
        key: 'justification',
        width: '35%',
        render: (text: string, record: DamageMechanism) => {
          if (editingKey === record.id) {
            return (
              <div style={{ position: 'relative' }}>
                <TextArea
                  value={text}
                  onChange={(e) =>
                    handleUpdate(record.id, { justification: e.target.value })
                  }
                  onSelect={saveCursor}
                  placeholder='Explain rate/susceptibility assignment'
                  rows={2}
                  style={{ width: '100%' }}
                />
                <Button
                  type='text'
                  size='small'
                  icon={<Expand size={13} />}
                  style={{
                    position: 'absolute',
                    bottom: 4,
                    right: 14,
                    padding: 2,
                    height: 'auto',
                    minWidth: 0,
                    opacity: 0.5,
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    hasRestoredRef.current = false
                    setExpandedJustification({
                      id: record.id,
                      name: record.name || 'Untitled',
                    })
                  }}
                />
              </div>
            )
          }
          return text || <span style={{ color: '#999' }}>No justification</span>
        },
      },
      ...(!readonly
        ? [
            {
              title: 'Actions',
              key: 'actions',
              width: '5%',
              render: (_: unknown, record: DamageMechanism) => (
                <Button
                  type='text'
                  danger
                  icon={<Trash2 size={14} />}
                  onClick={() => handleDelete(record.id)}
                />
              ),
            },
          ]
        : []),
    ],
    [
      readonly,
      stepId,
      zoneId,
      editingKey,
      rateUnit,
      handleUpdate,
      saveCursor,
      hasRestoredRef,
      handleDelete,
    ]
  )

  return (
    <Panel>
      <HelpText>
        Define the damage mechanisms expected in this zone. Each mechanism
        should include a rate or susceptibility rating and justification based
        on process conditions, material, and historical data.
      </HelpText>

      <AiFieldArea>
        {!readonly && (
          <div>
            <ActionButton
              type='dashed'
              icon={<Plus size={16} />}
              onClick={handleAdd}>
              Add Damage Mechanism
            </ActionButton>
            <AiPrefillFieldState
              meta={aiPrefill}
              field='damage_mechanisms'
              empty={isMissingPrefillValue('damage_mechanisms', mechanisms)}
            />
          </div>
        )}

        <Table
          dataSource={mechanisms}
          columns={columns}
          rowKey='id'
          pagination={false}
          size='small'
          onRow={
            readonly
              ? undefined
              : (record) => ({
                  onClick: () => setEditingKey(record.id),
                })
          }
          style={readonly ? undefined : { cursor: 'pointer' }}
          locale={{
            emptyText: readonly
              ? 'No damage mechanisms defined.'
              : 'No damage mechanisms defined yet. Pre-fill with AI or add one manually.',
          }}
        />
      </AiFieldArea>

      <Modal
        title={`Justification — ${expandedJustification?.name ?? ''}`}
        open={expandedJustification !== null}
        onCancel={() => setExpandedJustification(null)}
        footer={null}
        width={640}
        destroyOnClose>
        {expandedJustification && (
          <TextArea
            ref={createRestoreRef({ guard: hasRestoredRef })}
            value={
              mechanisms.find((m) => m.id === expandedJustification.id)
                ?.justification ?? ''
            }
            onChange={(e) =>
              handleUpdate(expandedJustification.id, {
                justification: e.target.value,
              })
            }
            onSelect={saveCursor}
            placeholder='Explain rate/susceptibility assignment'
            rows={10}
            style={{ width: '100%', marginTop: 8 }}
          />
        )}
      </Modal>
    </Panel>
  )
}
